import axios from "axios";
import React from "react";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { NextPageContext } from "next";
import { ArrowLeftIcon } from "lucide-react";

import FAQ from "@/components/FAQ";
import Layout from "@/components/layout";
import TeacherCard from "@/components/TeacherCard";
import { cn } from "@/utils/cn";
import { financeId } from "./masterclasses";
import mixpanel from "@/utils/mixpanel";
import Breadcrumb from "@/components/BreadCrumb";
import Head from "next/head";

export async function getServerSideProps(ctx: NextPageContext) {
  try {
    const { data: tags } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}categories/sub-categories/${financeId}`,
      {
        headers: { "Accept-Encoding": "gzip,deflate,compress" },
      }
    );

    const subCategory = ctx.query.category as string;
    let subCategoryId;
    if (subCategory && typeof subCategory === "string") {
      subCategoryId = tags.data.find(
        (tag: any) => tag.slug === subCategory
      )?.id;
    }

    let url = `${process.env.NEXT_PUBLIC_BASE_API}teachers?categoryId=${financeId}`;
    if (subCategoryId) {
      url += `&subCategoryId=${subCategoryId}`;
    }

    const { data: res } = await axios.get(url, {
      headers: { "Accept-Encoding": "gzip,deflate,compress" },
    });

    return {
      props: { teachers: res.data, tags: tags.data },
    };
  } catch (err) {
    return {
      props: { teachers: [], tags: [] },
    };
  }
}

export type Tag = { [key: string]: { name: string; id: string; slug: string } };

const page = ({ teachers, tags }: { teachers: any; tags: any }) => {
  const router = useRouter();
  const [activeCategory, setActiveCategory] = React.useState<{
    name: string;
    id: string;
    slug: string;
  } | null>(null);

  const filteredTeachers = teachers.filter((teacher: any) =>
    teacher.bootcamp.some((bx: any) =>
      bx.masterClass.some((mc: any) => mc.slots.length > 0)
    )
  );

  const tagsArray = tags || [];
  const TAGS: Tag = {};
  tagsArray.forEach((tag: any) => {
    TAGS[tag.slug] = {
      name: tag?.name,
      slug: tag?.slug,
      id: tag?.id,
    };
  });

  React.useEffect(() => {
    setActiveCategory(
      router.query.category ? TAGS[router.query.category.toString()] : null
    );
  }, [router.query.category]);

  return (
    <>
      <Head>
        <title>Meet Our Expert Trading Teachers | Tradewise</title>
        <meta
          name="description"
          content="Discover the experienced trading instructors at Tradewise. Learn from industry experts and elevate your skills through professional guidance and mentorship"
        />
      </Head>

      <Layout>
        <Breadcrumb classNames="lg:mb-1" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ease: "linear", duration: 0.25 }}
          className="flex flex-col gap-4 md:flex-row md:justify-between"
        >
          <div className="flex flex-row max-md:gap-4 justify-start items-center">
            <ArrowLeftIcon
              className="w-4 h-4 hover:cursor-pointer md:hidden"
              onClick={() => router.back()}
            />
            <h1 className="text-[#161C2B] max-lg:text-xl font-bold">
              Our Faculty
            </h1>
          </div>
          <div className="max-md:w-full overflow-x-auto hide_scrollbar max-md:flex max-md:gap-1 max-md:justify-evenly max-md:items-center md:space-x-4">
            <button
              onClick={() => router.push("/teachers")}
              className={cn(
                "rounded-full whitespace-nowrap text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none",

                !activeCategory
                  ? "bg-foreground !text-white hover:text-white"
                  : "bg-primary/10 text-foreground"
              )}
            >
              All
            </button>
            {tagsArray.filter((tag: any) => tag.name !== "Forex").map((tag: any) => (
              <button
                onClick={() => {
                  router.push(`/teachers?category=${tag.slug}`);
                  mixpanel.track("explore_category", {
                    page_location: "Our Experts",
                    page_referrer: document.referrer,
                    location: "tabs",
                    subcategory: activeCategory?.name,
                  });
                }}
                className={`rounded-full text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none whitespace-nowrap
                  ${
                    tag?.name === activeCategory?.name
                      ? "bg-foreground !text-white hover:text-white"
                      : "bg-primary/10 text-foreground"
                  }`}
                key={tag?.id}
              >
                {tag?.name}
              </button>
            ))}
          </div>
        </motion.div>
        <section className="mt-8 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 place-items-center gap-y-4 md:gap-5 mt-4 md:mt-6 transition-all w-fit mx-auto">
            {filteredTeachers.length !== 0 &&
              filteredTeachers?.map((item: any, index: number) => {
                if (!item.photo) return null;
                return (
                  <TeacherCard
                    key={item.id}
                    data={item}
                    initial={{ opacity: 0, translateY: 20 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    transition={{ duration: 0.3, type: "tween" }}
                  />
                );
              })}
          </div>
        </section>
        <FAQ isAccHidden={true} />
      </Layout>
    </>
  );
};

export default page;
